import React from 'react';
import { Button } from '@/components/ui/button';
import { Loader2, X, Info, RefreshCw, Code } from 'lucide-react';
import InspectModeOverlay from '@/components/InspectModeOverlay';

/**
 * PreviewIframe - WordPress iframe preview with loading/error states
 * Extracted from DesignPreviewPage.js
 */
const PreviewIframe = ({
  // Preview state
  previewUrl,
  previewLoading,
  setPreviewLoading,
  previewError,
  previewTimeout,
  // Iframe ref
  iframeRef,
  onIframeError,
  onRefresh,
  onSwitchToTechnical,
  // Inspect mode
  inspectMode,
  selectedElement,
  clearElementSelection,
  handleEditElement,
  toggleInspectMode,
  // Responsive
  responsiveViewMode,
}) => {
  const minHeight = responsiveViewMode === 'desktop' ? '800px' : '700px';

  return (
    <div 
      className="relative" 
      style={{ 
        minHeight,
        overflow: 'visible'
      }}
    >
      {/* Loading State */}
      {previewLoading && !previewError && !previewTimeout && (
        <div className="absolute inset-0 flex items-center justify-center bg-zinc-50 z-10">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600 mx-auto mb-3" />
            <p className="text-sm text-zinc-600">Loading WordPress preview...</p>
            <p className="text-xs text-zinc-400 mt-1">This may take a few seconds</p>
          </div>
        </div>
      )}
      
      {/* Error State */}
      {previewError && (
        <div className="flex items-center justify-center h-[700px] bg-red-50">
          <div className="text-center p-8">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <X className="w-8 h-8 text-red-600" />
            </div>
            <h3 className="text-lg font-semibold text-zinc-900 mb-2">Preview Error</h3>
            <p className="text-zinc-600 max-w-md mb-4">{previewError}</p>
            <Button onClick={onRefresh} className="bg-indigo-600 hover:bg-indigo-700">
              <RefreshCw className="w-4 h-4 mr-2" />
              Try Again
            </Button>
          </div>
        </div>
      )}
      
      {/* Timeout State */}
      {previewTimeout && !previewError && (
        <div className="flex items-center justify-center h-[700px] bg-amber-50">
          <div className="text-center p-8">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Info className="w-8 h-8 text-amber-600" />
            </div>
            <h3 className="text-lg font-semibold text-zinc-900 mb-2">Preview Taking Too Long</h3>
            <p className="text-zinc-600 max-w-md mb-4">
              The WordPress preview is taking longer than expected. 
              The staging site may be slow or unavailable.
            </p>
            <div className="flex gap-3 justify-center">
              <Button onClick={onRefresh} className="bg-indigo-600 hover:bg-indigo-700">
                <RefreshCw className="w-4 h-4 mr-2" />
                Retry
              </Button>
              <Button variant="outline" onClick={onSwitchToTechnical}>
                <Code className="w-4 h-4 mr-2" />
                View Technical
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Iframe or Token Loading */}
      {!previewError && !previewTimeout && (
        previewUrl ? (
          <div 
            className="iframe-container relative"
            style={{
              width: '100%',
              height: '100%',
              overflow: 'hidden'
            }}
          >
            <iframe
              ref={iframeRef}
              src={previewUrl}
              className="border-0 bg-white"
              style={{ 
                width: '100%',
                height: 'calc(100vh - 200px)', 
                minHeight: '700px'
              }}
              onLoad={() => setPreviewLoading(false)}
              onError={onIframeError}
              title="Design Preview"
            />
            {/* Inspect Mode Overlay */}
            <InspectModeOverlay
              selectedElement={selectedElement}
              onClearSelection={clearElementSelection}
              onEditElement={handleEditElement}
              isInspectMode={inspectMode}
              onToggleInspectMode={toggleInspectMode}
            />
          </div>
        ) : (
          <div className="flex items-center justify-center h-[600px] bg-zinc-50">
            <div className="text-center p-8">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Info className="w-8 h-8 text-amber-600" />
              </div>
              <h3 className="text-lg font-semibold text-zinc-900 mb-2">Generating Preview Token...</h3>
              <p className="text-zinc-600 max-w-md">
                Please wait while we prepare the preview.
              </p>
              <Loader2 className="w-6 h-6 animate-spin text-indigo-600 mx-auto mt-4" />
            </div>
          </div>
        )
      )}
    </div>
  );
};

export default PreviewIframe;
